import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, TextField} from '@mui/material';
import {useAppDispatch} from '../../../redux/storeExports';
import {getActiveOptOutBrokersList, retriggerRemovals} from '../../../redux/brokerService';
import AutoCompleteSelect from '../atom/AutoCompleteSelect';
import {BrokerOption} from "./FilterContainer";

// Types
interface FormState {
	muid: string;
	recordId: string;
	brokerNames: string[];
}

const INITIAL_FORM_STATE: FormState = {
	muid: '',
	recordId: '',
	brokerNames: [],
};

const ALL_BROKER_OPTION: BrokerOption = {
	title: 'All',
	id: 'all',
};

const RetriggerRemovalsPopup: React.FC = () => {
	const dispatch = useAppDispatch();

	// State
	const [dataBrokerOptions, setDataBrokerOptions] = useState<BrokerOption[]>([]);
	const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
	const [dialogOpen, setDialogOpen] = useState(false);

	// Fetch Brokers List
	const fetchActiveOptOutBrokersList = useCallback(async () => {
		try {
			const response = await dispatch(getActiveOptOutBrokersList());
			const brokers: BrokerOption[] = response.payload.brokers.map((broker: string) => ({
				title: broker,
				id: broker,
			}));
			setDataBrokerOptions([ALL_BROKER_OPTION, ...brokers]);
		} catch (error) {
			console.error('Failed to fetch active opt-out brokers:', error);
		}
	}, [dispatch]);

	useEffect(() => {
		if (dialogOpen) {
			fetchActiveOptOutBrokersList();
		}
	}, [dialogOpen, fetchActiveOptOutBrokersList]);

	// Form handlers
	const resetForm = useCallback(() => {
		setFormState(INITIAL_FORM_STATE);
	}, []);

	const handleMuidChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.trim();
		setFormState((prev) => ({...prev, muid: value}));
	}, []);

	const handleRecordIDChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.trim();
		setFormState((prev) => ({...prev, recordId: value}));
	}, []);

	const handleBrokerChange = useCallback((values: string[]) => {
		setFormState((prev) => ({
			...prev,
			brokerNames: values.includes(ALL_BROKER_OPTION.id)
				? dataBrokerOptions
					.filter(({id}) => id !== ALL_BROKER_OPTION.id)
					.map(({id}) => id)
				: values,
		}));
	}, [dataBrokerOptions]);

	// Dialog handlers
	const handleDialogOpen = useCallback(() => {
		setDialogOpen(true);
	}, []);

	const handleDialogClose = useCallback(() => {
		resetForm();
		setDialogOpen(false);
	}, [resetForm]);

	const handleRetriggerRemovals = useCallback(async () => {
		const {muid, recordId, brokerNames} = formState;

		if (!muid || brokerNames.length === 0) return;

		try {
			await dispatch(retriggerRemovals({
				muid: muid,
				record_id: recordId || null,											
				provider_names: brokerNames
			})).unwrap();
			handleDialogClose();
		} catch (error) {
			console.error('Failed to retrigger removals:', error);
		}
	}, [dispatch, formState, handleDialogClose]);

	// Memoized values
	const isSubmitDisabled = useMemo(() =>
			!formState.muid || formState.brokerNames.length === 0,
		[formState.muid, formState.brokerNames.length]
	);

	return (
		<div>
			<Button
				variant="contained"
				color="success"
				onClick={handleDialogOpen}
				sx={{minWidth: '150px'}}
			>
				Retrigger Opt-outs
			</Button>

			<Dialog
				open={dialogOpen}
				onClose={handleDialogClose}
				maxWidth="sm"
				fullWidth
			>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to retrigger opt-out for the selected records?
					</DialogContentText>

					<TextField
						value={formState.muid}
						onChange={handleMuidChange}
						label="MUID"
						size="small"
						fullWidth
						sx={{my: 3}}
						required
					/>

					<TextField
						value={formState.recordId}
						onChange={handleRecordIDChange}
						label="Record ID (Optional)"
						size="small"
						fullWidth
						sx={{my: 3}}
					/>

					<AutoCompleteSelect
						options={dataBrokerOptions}
						onChange={handleBrokerChange}
						label="Optout Only Brokers"
						value={formState.brokerNames}
					/>
				</DialogContent>

				<DialogActions>
					<Button onClick={handleDialogClose} color="primary">
						Cancel
					</Button>
					<Button
						onClick={handleRetriggerRemovals}
						color="primary"
						disabled={isSubmitDisabled}
					>
						Retrigger Opt-out
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default RetriggerRemovalsPopup;
